import { Box, Switch, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { ValueRow } from '~/components/toolbar/shared/StageToggleRows';
import { sliderSwitchSx } from '~/components/toolbar/tools/ContouringTools/drawingConstraints/constraintStyles';
import DvfRangeSlider from '~/components/dvf/DvfRangeSlider';
import { clampDvfDisplayRange } from '~/domain/dvf/dvfDisplayRange';
import { DvfField, dvfMaxMagnitude } from '~/domain/dvf/dvfField';
import {
    DVF_SPACING_CM_MAX,
    DVF_SPACING_CM_MIN,
    DVF_SPACING_CM_PRECISION,
    DVF_SPACING_CM_STEP,
} from '~/hooks/workflows/dvf/dvfSpacing';
import {
    deformationArrowSpacingMmState,
    deformationArrowsEnabledState,
    deformationDisplayRangeMmState,
    deformationGridEnabledState,
    deformationGridSpacingMmState,
    deformationHeatmapEnabledState,
} from '~/state/atoms/deformationDisplayAtoms';
import { cmToMm, mmToCm } from '~/utils/units';
import { infernoGradientCss } from '~/utils/colormap';

interface OverlayToggleProps {
    labelId: string;
    checked: boolean;
    onChange: (enabled: boolean) => void;
    testId: string;
}

export function OverlayToggle({
    labelId,
    checked,
    onChange,
    testId,
}: OverlayToggleProps) {
    const intl = useIntl();

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            minHeight={24}
        >
            <Typography fontSize={11}>
                <FormattedMessage id={labelId} />
            </Typography>
            <Switch
                disableRipple
                sx={sliderSwitchSx}
                checked={checked}
                onChange={(_, isChecked) => onChange(isChecked)}
                inputProps={{
                    'aria-label': intl.formatMessage({ id: labelId }),
                }}
                data-testid={testId}
            />
        </Box>
    );
}

interface SpacingFieldProps {
    spacingMm: number;
    onChange: (spacingMm: number) => void;
    testId: string;
}

function SpacingField({ spacingMm, onChange, testId }: SpacingFieldProps) {
    return (
        <ValueRow
            labelId="toolbar.deformationField.spacing"
            value={mmToCm(spacingMm)}
            onValueChange={(cm) => onChange(cmToMm(cm))}
            min={DVF_SPACING_CM_MIN}
            max={DVF_SPACING_CM_MAX}
            step={DVF_SPACING_CM_STEP}
            precision={DVF_SPACING_CM_PRECISION}
            unit="cm"
            dataTestId={testId}
        />
    );
}

function HeatmapLegend({ maxMm }: { maxMm: number }) {
    return (
        <Box display="flex" flexDirection="column" gap={0.25} pb={0.5}>
            <Box
                sx={{
                    height: 8,
                    borderRadius: 0.5,
                    background: infernoGradientCss('to right'),
                }}
                data-testid="dvf-heatmap-legend"
            />
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={10} color="text.secondary">
                    0
                </Typography>
                <Typography fontSize={10} color="text.secondary">
                    {`${mmToCm(maxMm).toFixed(2)} cm`}
                </Typography>
            </Box>
        </Box>
    );
}

export default function DeformationDisplayControls({
    field,
}: {
    field: DvfField;
}) {
    const [arrowsOn, setArrowsOn] = useRecoilState(
        deformationArrowsEnabledState
    );
    const [gridOn, setGridOn] = useRecoilState(deformationGridEnabledState);
    const [heatmapOn, setHeatmapOn] = useRecoilState(
        deformationHeatmapEnabledState
    );
    const [arrowSpacingMm, setArrowSpacingMm] = useRecoilState(
        deformationArrowSpacingMmState
    );
    const [gridSpacingMm, setGridSpacingMm] = useRecoilState(
        deformationGridSpacingMmState
    );
    const [displayRange, setDisplayRange] = useRecoilState(
        deformationDisplayRangeMmState
    );

    const maxMm = dvfMaxMagnitude(field);
    const range = useMemo(
        () =>
            clampDvfDisplayRange(
                displayRange?.lowMm,
                displayRange?.highMm,
                maxMm
            ),
        [displayRange, maxMm]
    );

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <OverlayToggle
                labelId="toolbar.deformationField.arrows"
                checked={arrowsOn}
                onChange={setArrowsOn}
                testId="dvf-arrows-toggle"
            />
            {arrowsOn && (
                <Box display="flex" flexDirection="column" gap={1} pb={0.5}>
                    <SpacingField
                        spacingMm={arrowSpacingMm}
                        onChange={setArrowSpacingMm}
                        testId="dvf-arrow-spacing"
                    />
                    <DvfRangeSlider
                        maxMm={maxMm}
                        range={range}
                        onChange={setDisplayRange}
                    />
                </Box>
            )}
            <OverlayToggle
                labelId="toolbar.deformationField.grid"
                checked={gridOn}
                onChange={setGridOn}
                testId="dvf-grid-toggle"
            />
            {gridOn && (
                <SpacingField
                    spacingMm={gridSpacingMm}
                    onChange={setGridSpacingMm}
                    testId="dvf-grid-spacing"
                />
            )}
            <OverlayToggle
                labelId="toolbar.deformationField.heatmap"
                checked={heatmapOn}
                onChange={setHeatmapOn}
                testId="dvf-heatmap-toggle"
            />
            {heatmapOn && <HeatmapLegend maxMm={maxMm} />}
        </Box>
    );
}
